import { useCallback, useMemo, useState } from "react";
import CancelSvg from "@assets/icons/new/close-2_18px.svg?react";
import { isScanFileUploadTaskContext } from "@custom-types/file-upload-type-guards";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { assert, GUID } from "@faro-lotv/foundation";
import { useCancelRevision } from "@hooks/data-management/use-cancel-revision";
import { useDialog } from "@components/common/dialog/dialog-provider";
import { Typography } from "@mui/material";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { uploadTasksSelector } from "@store/upload-tasks/upload-tasks-selector";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { useAppSelector, useAppDispatch } from "@store/store-helper";
import { FaroTextButton } from "@components/common/faro-text-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkflowState } from "@pages/data-management/data-management-types";
import {
  cancelUploads,
  onCancelImport,
} from "@pages/data-management/import-data/cancel-import-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { hasAnyDraftChangesSelector } from "@store/capture-tree/capture-tree-selectors";
import { useToast } from "@hooks/use-toast";
import { isRegisteringSelector } from "@pages/data-management/registered-data/registered-data-selectors";
import { sphereColors } from "@styles/common-colors";

/** Props for "Cancel import" Button */
interface Props extends BaseProjectIdProps {
  /** Workflow state of the project */
  state: WorkflowState;
  /** Flag if we're coming from the error view. */
  isFromErrorView: boolean;
  /** Callback to set whether to prevent the redirect to the Viewer when the published state is reached. */
  setShouldPreventRedirect: React.Dispatch<React.SetStateAction<boolean>>;
  /** Optional callback when the button is clicked, called before the main action. */
  onClickBefore?: () => void;
}

/** Button to cancel upload of scans */
export function CancelImportButton({
  projectId,
  state,
  isFromErrorView,
  setShouldPreventRedirect,
  onClickBefore,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { createDialog } = useDialog();
  const uploadTasks = useAppSelector(uploadTasksSelector);
  const { uploadManager } = useFileUploadContext();
  const cancelRevision = useCancelRevision();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const { showToast } = useToast();

  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });
  const registrationApiClient = useRegistrationApiClient({ projectId });

  const isRegistering = useAppSelector(isRegisteringSelector);
  const hasAnyDraftChanges = useAppSelector(hasAnyDraftChangesSelector);

  // The button is only visible in the wizard view during progress and in the upload error view.
  assert(["uploading", "uploadError", "processing", "registering", "publishing"].includes(state));
  const shouldUseUpload = state === "uploading" || state === "uploadError";

  /** Revision of the current Staging Area uploads in the current project. */
  const revisionIdForUpload = useMemo<GUID | null>(() => {
    for (const task of uploadTasks) {
      if (task.context.projectId === projectId &&
        isScanFileUploadTaskContext(task.context)) {
        return task.context.captureTreeRevisionId;
      }
    }
    return null;
  }, [projectId, uploadTasks]);

  /** Flag to prevent multiple cancel requests. */
  const [cancelInProgress, setCancelInProgress] = useState(false);

  /**
   * Flag to disable the button depending on the workflow state.
   */
  const isButtonDisabled = useMemo<boolean>(() => {
    // In state "registering", it is possible that there are no draft changes, if the user is re-publishing
    // the project, and the registration task hasn't started yet.
    // TODO TF-2048: Due to the current limitation the import cannot be cancelled in processing state.
    return cancelInProgress ||
      state === "processing" ||
      state === "publishing" ||
      (state === "uploading" && !revisionIdForUpload) ||
      (state !== "uploading" && state !== "uploadError" && state !== "registering" && !hasAnyDraftChanges);
  }, [
    cancelInProgress, hasAnyDraftChanges, revisionIdForUpload, state,
  ]);

  /**
   * Asks for confirmation, and cancels the running upload.
   */
  const onCancelUpload = useCallback(async (): Promise<void> => {
    if (!isFromErrorView && !(await createDialog(
      {
        title: "Cancel upload?",
        confirmText: "Cancel upload",
        closeText: "Continue upload",
      },
      <Typography
        sx={{
          fontSize: "14px",
          marginBottom: SPACE_ELEMENTS_OF_MODAL,
        }}
      >
        You are about to interrupt the current upload process.
        <span style={{marginTop: "8px", display: "block"}}>
          Any files that have been uploaded will be removed and you will need to start over with a new upload.
        </span>
      </Typography>
    ))) {
      return;
    }

    assert(revisionIdForUpload, "Revision ID for upload should be defined.");
    await cancelUploads(
      dispatch,
      projectApiClient,
      progressApiClient,
      handleErrorWithToast,
      setCancelInProgress,
      revisionIdForUpload,
      cancelRevision,
      uploadTasks,
      uploadManager,
      trackEvent,
      isFromErrorView,
      setShouldPreventRedirect
    );
  }, [
    cancelRevision,
    createDialog,
    dispatch,
    handleErrorWithToast,
    isFromErrorView,
    progressApiClient,
    projectApiClient,
    revisionIdForUpload,
    setShouldPreventRedirect,
    trackEvent,
    uploadManager,
    uploadTasks,
  ]);

  /** Ask for confirmation, then cancel all ongoing Staging Area uploads of the current project. */
  const onClick = useCallback(async (): Promise<void> => {
    if (onClickBefore) {
      onClickBefore();
    }

    if (shouldUseUpload) {
      await onCancelUpload();
    } else {
      await onCancelImport(
        dispatch,
        projectApiClient,
        progressApiClient,
        registrationApiClient,
        showToast,
        handleErrorWithToast,
        false /* shouldUseDiscard */,
        createDialog,
        setCancelInProgress,
        state,
        uploadTasks,
        uploadManager,
        cancelRevision,
        trackEvent,
        isRegistering,
        setShouldPreventRedirect
      );
    }
  }, [
    cancelRevision,
    createDialog,
    dispatch,
    handleErrorWithToast,
    isRegistering,
    onCancelUpload,
    onClickBefore,
    progressApiClient,
    projectApiClient,
    registrationApiClient,
    setShouldPreventRedirect,
    shouldUseUpload,
    showToast,
    state,
    trackEvent,
    uploadManager,
    uploadTasks,
  ]);

  let tooltip: string;
  if (isButtonDisabled) {
    tooltip = "The import cannot be canceled in the current step.";
  } else if (shouldUseUpload) {
    tooltip = "Cancel the current upload process.";
  } else {
    tooltip = "Cancel the current import process.";
  }

  return (
    <SphereTooltip
      // If there are multiple buttons, show them in the same row.
      boxProps={{
        sx: {
          display: "inline-block",
        },
      }}
      title={tooltip}
    >
      <FaroTextButton
        dataTestId="sa-cancel-import-tooltip"
        startIcon={ isFromErrorView ? undefined : <CancelSvg/> }
        onClick={() => void onClick()}
        isDisabled={isButtonDisabled}
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          backgroundColor: sphereColors.gray50,
          paddingLeft: isFromErrorView ? undefined : "15px",
          paddingRight: isFromErrorView ? undefined : "15px",
        }}
      >
        { isFromErrorView ? "Close" : "Cancel" }
      </FaroTextButton>
    </SphereTooltip>
  );
}
